import { FilterCreatorsDto } from '@/app/common/general-types/filters.dto';
import { interestsList } from '@/app/common/app-config/filter-models/interests';
import {
  FOLLOWER_RANGE_DEFAULT,
  FollowerRangeValue,
  isDefaultFollowerRange,
} from '@/app/common/app-config/filter-models/follower-range';
import {
  emptyPlatformPerformanceDraft,
  isDefaultPerformanceRange,
  PERFORMANCE_RANGE_DEFAULT,
  PerformanceRangeValue,
  PlatformPerformanceDraft,
} from '@/app/common/app-config/filter-models/performance-range';

export interface FindCreatorsCountryOption {
  id: string;
  name: string;
}

export interface FindCreatorsRatingOption {
  value: number;
  label: string;
}

export interface FindCreatorsFilterDraft {
  country: string | null;
  gender: number | null;
  rating: number | null;
  ageFrom: number | null;
  ageTo: number | null;
  isVerified: boolean;
  hasReviews: boolean;
  homeTownSearchTerm: string;
  interests: number[];
  tikTokFollowers: FollowerRangeValue | null;
  instagramFollowers: FollowerRangeValue | null;
  youtubeSubscribers: FollowerRangeValue | null;
  snapchatFollowers: FollowerRangeValue | null;
  tikTokPerformance: PlatformPerformanceDraft;
  instagramPerformance: PlatformPerformanceDraft;
  youtubePerformance: PlatformPerformanceDraft;
}

export const FIND_CREATORS_COUNTRY_OPTIONS: FindCreatorsCountryOption[] = [
  { id: 'GB', name: 'UK' },
  { id: 'US', name: 'USA' },
  { id: 'IE', name: 'Ireland' },
];

export const FIND_CREATORS_RATING_OPTIONS: FindCreatorsRatingOption[] = [
  { value: 3, label: '3+' },
  { value: 4, label: '4+' },
  { value: 4.5, label: '4.5+' },
];

const PERFORMANCE_DTO_KEYS = [
  'tikTokAverageEngagementFrom',
  'tikTokAverageViewsFrom',
  'tikTokAverageViewsTo',
  'tikTokTotalViewsFrom',
  'tikTokTotalViewsTo',
  'tikTokAverageLikesFrom',
  'tikTokAverageLikesTo',
  'tikTokTotalLikesFrom',
  'tikTokTotalLikesTo',
  'instagramAverageEngagementFrom',
  'instagramAverageViewsFrom',
  'instagramAverageViewsTo',
  'instagramTotalViewsFrom',
  'instagramTotalViewsTo',
  'instagramAverageLikesFrom',
  'instagramAverageLikesTo',
  'instagramTotalLikesFrom',
  'instagramTotalLikesTo',
  'youtubeAverageEngagementFrom',
  'youtubeAverageViewsFrom',
  'youtubeAverageViewsTo',
  'youtubeTotalViewsFrom',
  'youtubeTotalViewsTo',
  'youtubeAverageLikesFrom',
  'youtubeAverageLikesTo',
  'youtubeTotalLikesFrom',
  'youtubeTotalLikesTo',
] as const;

export const FIND_CREATORS_PROFILE_KEYS = [
  'country',
  'gender',
  'rating',
  'ageFrom',
  'ageTo',
  'isVerified',
  'hasReviews',
  'tikTokFollowersFrom',
  'tikTokFollowersTo',
  'instagramFollowersFrom',
  'instagramFollowersTo',
  'youtubeSubscribersFrom',
  'youtubeSubscribersTo',
  'snapchatFollowersFrom',
  'snapchatFollowersTo',
  'homeTownSearchTerm',
  'interests',
  'ageOptions',
  'tiktokFollowersRange',
  'instagramFollowersRange',
  'youtubeSubscribersRange',
  'snapchatFollowersRange',
  ...PERFORMANCE_DTO_KEYS,
] as const;

export type FindCreatorsProfileKey = (typeof FIND_CREATORS_PROFILE_KEYS)[number];

export const EMPTY_FIND_CREATORS_FILTER_DRAFT: FindCreatorsFilterDraft = {
  country: null,
  gender: null,
  rating: null,
  ageFrom: null,
  ageTo: null,
  isVerified: false,
  hasReviews: false,
  homeTownSearchTerm: '',
  interests: [],
  tikTokFollowers: null,
  instagramFollowers: null,
  youtubeSubscribers: null,
  snapchatFollowers: null,
  tikTokPerformance: emptyPlatformPerformanceDraft(),
  instagramPerformance: emptyPlatformPerformanceDraft(),
  youtubePerformance: emptyPlatformPerformanceDraft(),
};

export function followerRangeFromDto(
  from?: number | null,
  to?: number | null,
): FollowerRangeValue | null {
  if (from == null && to == null) {
    return null;
  }
  const range: FollowerRangeValue = {
    min: from ?? FOLLOWER_RANGE_DEFAULT.min,
    max: to ?? FOLLOWER_RANGE_DEFAULT.max,
  };
  return isDefaultFollowerRange(range) ? null : range;
}

export function performanceRangeFromDto(
  from?: number | null,
  to?: number | null,
): PerformanceRangeValue | null {
  if (from == null && to == null) {
    return null;
  }
  const range: PerformanceRangeValue = {
    min: from ?? PERFORMANCE_RANGE_DEFAULT.min,
    max: to ?? PERFORMANCE_RANGE_DEFAULT.max,
  };
  return isDefaultPerformanceRange(range) ? null : range;
}

function applyFollowerRangeToDto(
  dto: FilterCreatorsDto,
  range: FollowerRangeValue | null,
  fromKey: 'tikTokFollowersFrom' | 'instagramFollowersFrom' | 'youtubeSubscribersFrom' | 'snapchatFollowersFrom',
  toKey: 'tikTokFollowersTo' | 'instagramFollowersTo' | 'youtubeSubscribersTo' | 'snapchatFollowersTo',
): void {
  if (!range || isDefaultFollowerRange(range)) {
    return;
  }
  dto[fromKey] = range.min;
  dto[toKey] = range.max;
}

function applyPerformanceRangeToDto(
  dto: FilterCreatorsDto,
  range: PerformanceRangeValue | null,
  fromKey: string,
  toKey: string,
): void {
  if (!range || isDefaultPerformanceRange(range)) {
    return;
  }
  (dto as Record<string, number>)[fromKey] = range.min;
  (dto as Record<string, number>)[toKey] = range.max;
}

function applyPlatformPerformanceToDto(
  dto: FilterCreatorsDto,
  draft: PlatformPerformanceDraft,
  prefix: 'tikTok' | 'instagram' | 'youtube',
): void {
  if (draft.averageEngagement != null) {
    (dto as Record<string, number>)[`${prefix}AverageEngagementFrom`] = draft.averageEngagement;
  }
  applyPerformanceRangeToDto(dto, draft.averageViews, `${prefix}AverageViewsFrom`, `${prefix}AverageViewsTo`);
  applyPerformanceRangeToDto(dto, draft.totalViews, `${prefix}TotalViewsFrom`, `${prefix}TotalViewsTo`);
  applyPerformanceRangeToDto(dto, draft.averageLikes, `${prefix}AverageLikesFrom`, `${prefix}AverageLikesTo`);
  applyPerformanceRangeToDto(dto, draft.totalLikes, `${prefix}TotalLikesFrom`, `${prefix}TotalLikesTo`);
}

function platformPerformanceFromDto(
  filters: FilterCreatorsDto,
  prefix: 'tikTok' | 'instagram' | 'youtube',
): PlatformPerformanceDraft {
  const record = filters as Record<string, number | null | undefined>;
  return {
    averageEngagement: typeof record[`${prefix}AverageEngagementFrom`] === 'number'
      ? record[`${prefix}AverageEngagementFrom`] as number
      : null,
    averageViews: performanceRangeFromDto(record[`${prefix}AverageViewsFrom`], record[`${prefix}AverageViewsTo`]),
    totalViews: performanceRangeFromDto(record[`${prefix}TotalViewsFrom`], record[`${prefix}TotalViewsTo`]),
    averageLikes: performanceRangeFromDto(record[`${prefix}AverageLikesFrom`], record[`${prefix}AverageLikesTo`]),
    totalLikes: performanceRangeFromDto(record[`${prefix}TotalLikesFrom`], record[`${prefix}TotalLikesTo`]),
  };
}

export function emptyFindCreatorsFilterDraft(): FindCreatorsFilterDraft {
  return {
    ...EMPTY_FIND_CREATORS_FILTER_DRAFT,
    interests: [],
    tikTokPerformance: emptyPlatformPerformanceDraft(),
    instagramPerformance: emptyPlatformPerformanceDraft(),
    youtubePerformance: emptyPlatformPerformanceDraft(),
  };
}

export function findCreatorsDraftToDto(draft: FindCreatorsFilterDraft): FilterCreatorsDto {
  const dto: FilterCreatorsDto = {};

  if (draft.country) {
    dto.country = [draft.country];
  }
  if (draft.gender != null) {
    dto.gender = [draft.gender];
  }
  if (draft.rating != null) {
    dto.rating = draft.rating;
  }
  if (draft.ageFrom != null) {
    dto.ageFrom = draft.ageFrom;
  }
  if (draft.ageTo != null) {
    dto.ageTo = draft.ageTo;
  }
  if (draft.isVerified) {
    dto.isVerified = true;
  }
  if (draft.hasReviews) {
    dto.hasReviews = true;
  }
  const hometown = draft.homeTownSearchTerm.trim();
  if (hometown) {
    dto.homeTownSearchTerm = hometown.toLowerCase();
  }
  if (draft.interests.length > 0) {
    dto.interests = [...draft.interests];
  }
  applyFollowerRangeToDto(dto, draft.tikTokFollowers, 'tikTokFollowersFrom', 'tikTokFollowersTo');
  applyFollowerRangeToDto(dto, draft.instagramFollowers, 'instagramFollowersFrom', 'instagramFollowersTo');
  applyFollowerRangeToDto(dto, draft.youtubeSubscribers, 'youtubeSubscribersFrom', 'youtubeSubscribersTo');
  applyFollowerRangeToDto(dto, draft.snapchatFollowers, 'snapchatFollowersFrom', 'snapchatFollowersTo');
  applyPlatformPerformanceToDto(dto, draft.tikTokPerformance, 'tikTok');
  applyPlatformPerformanceToDto(dto, draft.instagramPerformance, 'instagram');
  applyPlatformPerformanceToDto(dto, draft.youtubePerformance, 'youtube');

  return dto;
}

export function findCreatorsDtoToDraft(filters: FilterCreatorsDto): FindCreatorsFilterDraft {
  const genderRaw = filters.gender;
  let gender: number | null = null;
  if (genderRaw !== undefined && genderRaw !== null) {
    gender = Array.isArray(genderRaw) ? Number(genderRaw[0]) : Number(genderRaw);
    if (!Number.isFinite(gender)) {
      gender = null;
    }
  }

  return {
    country: filters.country?.[0] ?? null,
    gender,
    rating: filters.rating ?? null,
    ageFrom: typeof filters.ageFrom === 'number' ? filters.ageFrom : null,
    ageTo: typeof filters.ageTo === 'number' ? filters.ageTo : null,
    isVerified: filters.isVerified === true,
    hasReviews: filters.hasReviews === true,
    homeTownSearchTerm: filters.homeTownSearchTerm ?? '',
    interests: Array.isArray(filters.interests) ? [...filters.interests] : [],
    tikTokFollowers: followerRangeFromDto(filters.tikTokFollowersFrom, filters.tikTokFollowersTo),
    instagramFollowers: followerRangeFromDto(filters.instagramFollowersFrom, filters.instagramFollowersTo),
    youtubeSubscribers: followerRangeFromDto(filters.youtubeSubscribersFrom, filters.youtubeSubscribersTo),
    snapchatFollowers: followerRangeFromDto(filters.snapchatFollowersFrom, filters.snapchatFollowersTo),
    tikTokPerformance: platformPerformanceFromDto(filters, 'tikTok'),
    instagramPerformance: platformPerformanceFromDto(filters, 'instagram'),
    youtubePerformance: platformPerformanceFromDto(filters, 'youtube'),
  };
}

export function stripProfileFilters(filters: FilterCreatorsDto): FilterCreatorsDto {
  const next: FilterCreatorsDto = { ...filters };
  for (const key of FIND_CREATORS_PROFILE_KEYS) {
    delete next[key];
  }
  return next;
}

export function mergeProfileFilters(
  current: FilterCreatorsDto,
  profile: FilterCreatorsDto,
): FilterCreatorsDto {
  return { ...stripProfileFilters(current), ...profile };
}

export function interestNames(ids: number[]): string {
  return ids
    .map((id) => interestsList.find((item) => item.id === id)?.name ?? '')
    .filter((name) => name.length > 0)
    .join(', ');
}
